import React, { useEffect, useState } from 'react';
import { RepairService, Model } from '../types/BookingTypes';
import { useCart } from '../context/CartContext';
import ServiceDetailsModal from './ServiceDetailsModal';
import ReviewList from './ReviewList';
import { ModelReview, getModelReviews } from '../services/orderService';
import { isLoggedIn } from '../services/authService';
import { useNavigate, useLocation } from 'react-router-dom';

interface ServiceSelectorProps {
  services: RepairService[];
  selectedServices: RepairService[];
  onSelect: (services: RepairService[]) => void;
  onBack?: () => void;
  modelName?: string;
  model: Model;
}

const ServiceSelector: React.FC<ServiceSelectorProps> = ({ 
  services, 
  selectedServices, 
  onSelect,
  onBack,
  modelName,
  model
}) => {
  const { addToCart, removeFromCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<RepairService | null>(null);
  const [reviews, setReviews] = useState<ModelReview[]>([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(true);


  useEffect(() => {
    const fetchReviews = async () => {
      if (model.id) {
        setIsLoadingReviews(true);
        try {
          const modelReviews = await getModelReviews(model.id);
          setReviews(modelReviews);
        } catch (error) {
          console.error('Error fetching reviews:', error);
        } finally {
          setIsLoadingReviews(false);
        }
      }
    };

    fetchReviews();
  }, [model.id]);

  const handleAuthenticationCheck = () => {
    if (!isLoggedIn()) {
      // Store current location to redirect back after login
      navigate('/login', { 
        state: { 
          from: location.pathname,
          model: model,
          selectedServices: selectedServices
        } 
      });
      return false;
    }
    return true;
  };

  const handleToggle = (service: RepairService) => {
    // Check authentication first
    if (!handleAuthenticationCheck()) {
      return;
    }

    const isSelected = selectedServices.some(s => s.id === service.id);
    const newServices = isSelected 
      ? selectedServices.filter(s => s.id !== service.id)
      : [...selectedServices, service];
    
    // Update local state immediately for responsive UI
    onSelect(newServices);
    
    // Fire and forget - instant operations
    if (isSelected) {
      // Remove from cart using optimistic removal
      removeFromCart(model.id, service.id);
    } else {
      // Add to cart - only add the new service
      addToCart(model, [service]);
    }
  };

  const handleServiceClick = (service: RepairService) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  return (
    <>
      <div className="space-y-6">
        {/* Services Section */}
        <div>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              {onBack && (
                <button
                  onClick={onBack}
                  className="mr-3 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white shadow-sm hover:shadow-md active:scale-95 transition-all"
                >
                  <i className="fas fa-arrow-left text-gray-600 text-sm"></i>
                </button>
              )}
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 bg-minto-500 rounded-full" />
                <h3 className="text-base md:text-lg font-semibold text-gray-900 flex items-center gap-1.5">
                  What are you looking for? <span className="text-gray-400 font-normal">|</span>
                  {model.image && (
                    <img src={model.image} alt={modelName} className="w-5 h-5 object-contain inline-block" />
                  )}
                  <span className="text-gray-400 font-normal">{modelName}</span>
                </h3>
              </div>
            </div>
            <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full shadow-sm">{services.length} services</span>
          </div>
          <div className="flex items-center justify-center gap-2 mb-3 px-4 py-2 rounded-xl animate-pulse" style={{ backgroundColor: '#fdf2f8', border: '1.5px solid #e879a8' }}>
            <i className="fas fa-map-marker-alt" style={{ color: '#d6006e', fontSize: '14px' }}></i>
            <span className="font-bold" style={{ color: '#a8005a', fontSize: '16px' }}>Currently serving in <span className="font-extrabold" style={{ color: '#d6006e' }}>Mumbai</span> only</span>
          </div>

          {/* Services List */}
          <div className="space-y-3 md:space-y-4">
            {[...services].sort((a, b) => {
              const aIsOEM = a.serviceName.toUpperCase().includes('OEM') ? 0 : 1;
              const bIsOEM = b.serviceName.toUpperCase().includes('OEM') ? 0 : 1;
              return aIsOEM - bIsOEM;
            }).map((service) => {
              const isSelected = selectedServices.some(s => s.id === service.id);
              return (
                <div
                  key={service.id}
                  className={`relative w-full flex flex-col rounded-lg md:rounded-xl transition-all duration-300 overflow-hidden ${
                    isSelected
                      ? 'bg-white border-2 border-minto-500 shadow-lg shadow-minto-100'
                      : service.serviceName.toUpperCase().includes('OEM')
                        ? 'bg-white border border-minto-400 shadow-md hover:shadow-lg hover:-translate-y-0.5'
                        : 'bg-white border border-gray-100 shadow-md hover:shadow-lg hover:-translate-y-0.5'
                  }`}
                >

                  {/* Recommended Header Tab */}
                  {service.serviceName.toUpperCase().includes('OEM') && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 px-3 py-1.5" style={{ background: '#e8296a', clipPath: 'polygon(0 0, 100% 0, calc(100% - 10px) 100%, 0 100%)', width: 'fit-content', paddingRight: '20px' }}>
                        <i className="fas fa-thumbs-up text-white" style={{ fontSize: '10px' }}></i>
                        <span className="text-white font-semibold tracking-wide" style={{ fontSize: '11px' }}>Recommended</span>
                      </div>
                      <span className="text-gray-900 pr-3" style={{ fontSize: '9px' }}>OEM: Made to original specification by an independent manufacturer</span>
                    </div>
                  )}

                  {/* Budget Header Tab */}
                  {service.serviceName.toUpperCase().includes('BASIC') && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5" style={{ background: '#374151', clipPath: 'polygon(0 0, 100% 0, calc(100% - 10px) 100%, 0 100%)', width: 'fit-content', paddingRight: '20px' }}>
                      <i className="fas fa-tag text-white" style={{ fontSize: '10px' }}></i>
                      <span className="text-white font-semibold tracking-wide" style={{ fontSize: '11px' }}>Budget</span>
                    </div>
                  )}

                  {/* Main Content */}
                  <div className="px-3 md:px-4 pt-2 pb-3 md:pb-4 flex items-start">
                    <div className="flex-shrink-0 w-16 h-16 md:w-18 md:h-18 bg-gray-50 rounded-xl p-1.5 mr-2.5 md:mr-3 flex items-center justify-center border border-gray-200">
                      <img
                        src={service.serviceImage}
                        alt={service.serviceName}
                        className="w-full h-full object-contain"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Row 1: Name + Price */}
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="min-w-0">
                          <h4 className="font-semibold text-gray-900 text-lg md:text-xl line-clamp-1">{service.serviceName}</h4>
                        </div>
                        <span className="flex-shrink-0 text-lg md:text-xl font-bold text-gray-900">₹{Math.round(service.finalPrice || (service.actualPrice + (service.serviceCost || 0) + ((service.serviceCost || 0) * 0.18)))}</span>
                      </div>
                      {/* Row 2: Save badge + i-Care strikethrough */}
                      {service.actualBrandPrice && (() => {
                        const ourPrice = Math.round(service.finalPrice || (service.actualPrice + (service.serviceCost || 0) + ((service.serviceCost || 0) * 0.18)));
                        const saving = Math.round(((service.actualBrandPrice - ourPrice) / service.actualBrandPrice) * 100);
                        return (
                          <div className="flex items-center justify-between gap-2 mt-1">
                            {saving > 0 ? (
                              <span className="inline-flex items-center gap-1 text-white font-semibold rounded-full" style={{ background: '#111827', fontSize: '10px', padding: '2px 8px' }}>
                                <i className="fas fa-tag" style={{ fontSize: '8px' }}></i>
                                Save {saving}%
                              </span>
                            ) : <span />}
                            <span className="text-gray-900 flex-shrink-0" style={{ fontSize: '11px' }}>
                              i-Care ~₹{service.actualBrandPrice.toLocaleString('en-IN')}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Basic Warning */}
                  {service.serviceName.toUpperCase().includes('BASIC') && (
                    <div className="mx-3 md:mx-4 mb-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#fff7ed', border: '1px solid #fed7aa' }}>
                      <div className="flex items-start gap-2" style={{ color: '#ea580c' }}>
                        <i className="fas fa-exclamation-triangle text-xs mt-0.5"></i>
                        <span className="text-xs font-medium">Budget option — no warranty. I understand the quality risk and want to save money.</span>
                      </div>
                    </div>
                  )}

                  {/* Warranty + Repair Time — separate boxes */}
                  <div className="mx-3 md:mx-4 mb-2 flex gap-2">
                    {/* Warranty box */}
                    <div className="flex-1 rounded-xl overflow-hidden" style={{ border: `1px solid ${service.warrantyPeriod > 0 ? '#f9a8d4' : '#fca5a5'}` }}>
                      <div className="flex items-center gap-1.5 px-3 py-1.5" style={{ background: service.warrantyPeriod > 0 ? '#fce7f3' : '#fee2e2' }}>
                        <i className="fas fa-shield-alt text-gray-900" style={{ fontSize: '11px' }}></i>
                        <span className="font-semibold text-gray-900" style={{ fontSize: '12px' }}>
                          {service.warrantyPeriod > 0 ? `Warranty (${service.warrantyPeriod} Days)` : 'Warranty'}
                        </span>
                      </div>
                      <div className="px-3 py-2 bg-white">
                        <span className="font-semibold block text-gray-900" style={{ fontSize: '13px' }}>
                          {service.warrantyPeriod > 0
                            ? `Till ${new Date(Date.now() + (service.warrantyPeriod * 24 * 60 * 60 * 1000)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
                            : 'Not included'}
                        </span>
                      </div>
                    </div>
                    {/* Repair in box */}
                    <div className="flex-1 rounded-xl overflow-hidden border border-gray-200">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-200">
                        <i className="fas fa-clock text-gray-900" style={{ fontSize: '11px' }}></i>
                        <span className="text-gray-900 font-semibold" style={{ fontSize: '12px' }}>Repair in</span>
                      </div>
                      <div className="px-3 py-2 bg-white">
                        <span className="font-semibold text-gray-900 block" style={{ fontSize: '13px' }}>{service.serviceDuration} mins <span className="font-normal text-gray-900">(Doorstep)</span></span>
                      </div>
                    </div>
                  </div>

                  {/* Genuine Parts — iPhone Settings verification */}
                  {service.serviceName.toUpperCase().includes('OEM') && (
                    <>
                      <div className="mx-3 md:mx-4 mb-1 px-3 py-2 bg-gray-100 rounded-lg">
                        <div className="flex items-center gap-2 text-gray-900">
                          <i className="fas fa-mobile-alt text-xs"></i>
                          <span className="font-medium" style={{ fontSize: '9px' }}>Shows as <span className="font-semibold">"Genuine Part"</span> in iPhone Settings → General → About</span>
                        </div>
                      </div>
                      <p className="mx-3 md:mx-4 mb-2 text-gray-700" style={{ fontSize: '9px', lineHeight: '1.4' }}>
                        ★ i-Care price is for reference only. Minto Repairs is not affiliated with Apple Inc.
                      </p>
                    </>
                  )}

                  {/* Footer */}
                  <div className="px-3 md:px-4 py-2 border-t border-gray-100 flex items-center justify-between">
                    <button
                      onClick={() => handleServiceClick(service)}
                      className="text-sm font-semibold flex items-center justify-center gap-1.5 px-4 md:px-5 py-2 md:py-2.5 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors min-w-[130px] md:min-w-[150px]"
                    >
                      <i className="fas fa-info-circle"></i>
                      View Details
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (service) {
                          handleToggle(service);
                          if (!isSelected && isLoggedIn()) {
                            // @ts-ignore
                            window.gtag_report_conversion();
                          }
                        }
                      }}
                      className={`px-4 md:px-5 py-2 md:py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center min-w-[130px] md:min-w-[150px] ${
                        isSelected 
                          ? 'bg-red-50 text-red-500 hover:bg-red-100 active:bg-red-200 border border-red-200' 
                          : 'bg-minto-500 text-white hover:bg-minto-600 active:bg-minto-700 shadow-sm hover:shadow-md'
                      }`}
                    >
                      {isSelected ? (
                        <>
                          <i className="fas fa-trash-alt mr-1.5 text-xs"></i>
                          Remove
                        </>
                      ) : (
                        <>
                          <i className="fas fa-cart-plus mr-1.5 text-xs"></i>
                          Add to Cart
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Reviews Section */}
        {reviews && reviews.length > 0 && (
          <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <i className="fas fa-star text-yellow-400 text-sm"></i>
              <h3 className="text-sm md:text-base font-semibold text-gray-800">Customer Reviews</h3>
            </div>
            <div className="p-4 mb-20">
              <ReviewList reviews={reviews} isLoading={isLoadingReviews} />
            </div>
          </div>
        )}
      </div>

      {selectedService && (
        <ServiceDetailsModal
          isOpen={!!selectedService}
          service={selectedService}
          onClose={() => setSelectedService(null)}
        />
      )}
    </>
  );
};

export default ServiceSelector; 